import { useState, useEffect } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { usePlatformAuditLogs } from '@/hooks/usePlatformAuditLogs';

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatNumber(n: number): string {
  return n.toLocaleString('de-DE');
}

function truncateDetails(details: Record<string, unknown>, maxLen = 80): string {
  const str = JSON.stringify(details);
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen) + '\u2026';
}

const PAGE_SIZE = 20;

export function PlatformAuditLogs() {
  const [actionInput, setActionInput] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);

  // Debounce action input
  useEffect(() => {
    const timer = setTimeout(() => {
      setActionFilter(actionInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [actionInput]);

  const { data, isLoading, isError, error, refetch } = usePlatformAuditLogs({
    page,
    pageSize: PAGE_SIZE,
    action: actionFilter || undefined,
    from: fromDate ? `${fromDate}T00:00:00Z` : undefined,
    to: toDate ? `${toDate}T23:59:59Z` : undefined,
  });

  const totalPages = data?.pagination.totalPages ?? 0;

  function handleFromChange(value: string) {
    setFromDate(value);
    setPage(1);
  }

  function handleToChange(value: string) {
    setToDate(value);
    setPage(1);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">Audit-Logs</h1>
      <p className="mt-2 text-muted-foreground">Alle Plattform-Aktivitäten protokolliert.</p>

      {/* Filters */}
      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Action filtern..."
            value={actionInput}
            onChange={(e) => setActionInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <label className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Von</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => handleFromChange(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Bis</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => handleToChange(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          />
        </label>
      </div>

      {/* Table */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Log-Einträge
              {data && (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({formatNumber(data.pagination.total)} gesamt)
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-12 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center gap-3 py-8">
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{error instanceof Error ? error.message : 'Fehler beim Laden der Audit-Logs'}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Erneut versuchen
                </Button>
              </div>
            ) : !data?.logs.length ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Keine Audit-Log-Einträge gefunden.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">Zeitstempel</th>
                      <th className="pb-2 font-medium">Aktion</th>
                      <th className="pb-2 font-medium">Ressource</th>
                      <th className="pb-2 font-medium">Details</th>
                      <th className="pb-2 font-medium">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.logs.map((log) => (
                      <tr key={log.id} className="border-b last:border-0">
                        <td className="py-2 whitespace-nowrap text-muted-foreground">
                          {formatDateTime(log.createdAt)}
                        </td>
                        <td className="py-2 font-mono text-xs">{log.action}</td>
                        <td className="py-2">{log.resourceType}</td>
                        <td className="py-2 max-w-[200px]">
                          <span
                            className="font-mono text-xs text-muted-foreground truncate block"
                            title={JSON.stringify(log.details, null, 2)}
                          >
                            {truncateDetails(log.details)}
                          </span>
                        </td>
                        <td className="py-2 font-mono text-xs whitespace-nowrap">{log.ipAddress}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pagination */}
      {data && totalPages > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Seite {data.pagination.page} von {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Zurück
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Weiter
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
